(() => {
  const page = document.querySelector('[data-aurelia-size-guide-page]');
  if (!page) return;

  const root = page.querySelector('[data-asg-root]');
  if (!root || root.dataset.asgPageInitialized === 'true') return;
  root.dataset.asgPageInitialized = 'true';

  const marketCountry = String(page.dataset.marketCountry || 'AU').toUpperCase();
  const typeSelect = root.querySelector('[data-asg-product-type]');
  const typeField = root.querySelector('[data-asg-type-field]');
  const fitField = root.querySelector('[data-asg-fit-field]');
  const fitSelect = root.querySelector('[data-asg-fit]');
  const contextBox = root.querySelector('[data-asg-context]');
  const productTitle = root.querySelector('[data-asg-product-title]');
  const productSummary = root.querySelector('[data-asg-product-summary]');
  const productPill = root.querySelector('[data-asg-product-pill]');
  const changeTypeButton = root.querySelector('[data-asg-change-type]');
  const recommendButton = root.querySelector('[data-asg-recommend]');
  const resetButton = root.querySelector('[data-asg-reset]');
  const warningBox = root.querySelector('[data-asg-warning]');
  const resultBox = root.querySelector('[data-asg-result]');
  const resultSize = root.querySelector('[data-asg-result-size]');
  const resultDetails = root.querySelector('[data-asg-result-details]');
  const resultReason = root.querySelector('[data-asg-result-reason]');
  const resultNote = root.querySelector('[data-asg-result-note]');
  const unitButtons = [...root.querySelectorAll('[data-asg-unit]')];
  const unitLabels = [...root.querySelectorAll('[data-asg-unit-label]')];

  if (!typeSelect || !recommendButton || !resetButton || !warningBox || !resultBox || !resultSize) return;

  if (!typeSelect.querySelector('option[value="footwear"]')) {
    const footwearOption = document.createElement('option');
    footwearOption.value = 'footwear';
    footwearOption.textContent = 'Shoes & footwear';
    const hatsOption = typeSelect.querySelector('option[value="sun_hats"]');
    typeSelect.insertBefore(footwearOption, hatsOption || null);
  }

  const measurementGrid = root.querySelector('[data-measure-field="bust"]')?.parentElement;
  if (measurementGrid && !root.querySelector('[data-measure-field="foot_length"]')) {
    measurementGrid.insertAdjacentHTML('beforeend', `
      <div hidden data-measure-field="foot_length" class="asg-field">
        <label for="asg-foot-length-page">
          Foot length <span data-asg-unit-label>(cm)</span> <span class="asg-required">*</span>
        </label>
        <input
          id="asg-foot-length-page"
          data-measure="foot_length"
          type="number"
          min="0"
          step="0.1"
          inputmode="decimal"
          placeholder="e.g. 24.1"
        >
        <small>Measure both feet from the back of the heel to the longest toe and enter the longer measurement.</small>
      </div>
    `);
  }

  const measureFields = [...root.querySelectorAll('[data-measure-field]')];
  const measureInputs = [...root.querySelectorAll('[data-measure]')];

  const typeConfig = {
    tops: {
      label: 'Tops, shirts & pullovers',
      summary: 'Bust determines the size; height helps estimate coverage.',
      required: ['bust'], optional: ['height'], primary: ['bust']
    },
    jackets: {
      label: 'Jackets & zip layers',
      summary: 'Bust and waist matter, especially when layering.',
      required: ['bust', 'waist'], optional: ['height'], primary: ['bust', 'waist']
    },
    dresses: {
      label: 'Dresses & tunics',
      summary: 'Bust, waist and hips are combined for the best fit.',
      required: ['bust', 'waist', 'hips'], optional: ['height'], primary: ['bust', 'waist', 'hips']
    },
    pants: {
      label: 'Pants & shorts',
      summary: 'Waist and hips determine the size; inside leg helps with length.',
      required: ['waist', 'hips'], optional: ['inseam'], primary: ['waist', 'hips']
    },
    leggings: {
      label: 'Leggings',
      summary: 'Waist and hips determine the size and compression.',
      required: ['waist', 'hips'], optional: [], primary: ['waist', 'hips']
    },
    swimwear: {
      label: 'Swimwear',
      summary: 'Bust, waist and hips determine the size; height helps with torso length.',
      required: ['bust', 'waist', 'hips'], optional: ['height'], primary: ['bust', 'waist', 'hips']
    },
    footwear: {
      label: 'Shoes & footwear',
      summary: 'Foot length determines the shoe size. Measure both feet and use the longer measurement.',
      required: ['foot_length'], optional: [], primary: ['foot_length'], footwear: true
    },
    sun_hats: {
      label: 'Sun hats & visors',
      summary: 'Only head circumference is needed.',
      required: ['head'], optional: [], primary: ['head'], accessory: true
    },
    arm_sleeves: {
      label: 'Arm sleeves',
      summary: 'Only the circumference of the fullest part of the upper arm is needed.',
      required: ['bicep'], optional: [], primary: ['bicep'], accessory: true
    },
    gloves: {
      label: 'Sun gloves',
      summary: 'Only palm circumference is needed.',
      required: ['palm'], optional: [], primary: ['palm'], accessory: true
    },
    face_neck: {
      label: 'Face & neck accessories',
      summary: 'This product is normally One Size or adjustable.',
      required: [], optional: [], primary: [], oneSize: true
    },
    one_size_clothing: {
      label: 'One-size clothing',
      summary: 'This garment uses one flexible size; check its dimensions and coverage.',
      required: [], optional: [], primary: [], oneSize: true
    },
    no_size: {
      label: 'No size required',
      summary: 'No body measurements are required for this product.',
      required: [], optional: [], primary: [], noSize: true
    }
  };

  const auChart = [
    { au: 4, bust: 80, waist: 62, hips: 90 },
    { au: 6, bust: 85, waist: 67, hips: 95 },
    { au: 8, bust: 90, waist: 72, hips: 100 },
    { au: 10, bust: 95, waist: 77, hips: 105 },
    { au: 12, bust: 100, waist: 82, hips: 110 },
    { au: 14, bust: 105, waist: 87, hips: 115 },
    { au: 16, bust: 110, waist: 92, hips: 120 },
    { au: 18, bust: 115, waist: 97, hips: 125 },
    { au: 20, bust: 120, waist: 102, hips: 130 },
    { au: 22, bust: 125, waist: 107, hips: 135 },
    { au: 24, bust: 130, waist: 112, hips: 140 }
  ];

  const defaultFootwearChart = [
    { size: 'AU 4', maxFootLengthCm: 20.8 },
    { size: 'AU 4.5', maxFootLengthCm: 21.3 },
    { size: 'AU 5', maxFootLengthCm: 21.6 },
    { size: 'AU 5.5', maxFootLengthCm: 22.2 },
    { size: 'AU 6', maxFootLengthCm: 22.5 },
    { size: 'AU 6.5', maxFootLengthCm: 23.0 },
    { size: 'AU 7', maxFootLengthCm: 23.5 },
    { size: 'AU 7.5', maxFootLengthCm: 24.1 },
    { size: 'AU 8', maxFootLengthCm: 24.4 },
    { size: 'AU 8.5', maxFootLengthCm: 24.8 },
    { size: 'AU 9', maxFootLengthCm: 25.4 },
    { size: 'AU 9.5', maxFootLengthCm: 25.7 },
    { size: 'AU 10', maxFootLengthCm: 26.0 },
    { size: 'AU 10.5', maxFootLengthCm: 26.7 },
    { size: 'AU 11', maxFootLengthCm: 27.0 },
    { size: 'AU 12', maxFootLengthCm: 27.6 }
  ];

  const measurementLabels = {
    bust: 'Bust', waist: 'Waist', hips: 'Hips', height: 'Height', inseam: 'Inside leg',
    head: 'Head', bicep: 'Bicep', palm: 'Palm', foot_length: 'Foot length'
  };

  let currentUnit = 'cm';
  let currentType = '';

  const formatMarketSize = (value, productType = currentType) => {
    const raw = String(value || '').trim();
    const match = raw.match(/^AU\s*(-?\d+(?:\.\d+)?)(?:\s*[-–—]\s*(-?\d+(?:\.\d+)?))?$/i);
    if (!match || marketCountry === 'AU') return raw;

    const footwear = productType === 'footwear';
    let prefix = 'AU';
    let offset = 0;

    if (marketCountry === 'GB') {
      prefix = 'UK';
      offset = footwear ? -2 : 0;
    } else if (marketCountry === 'US' || marketCountry === 'NZ' || marketCountry === 'SG') {
      prefix = 'US';
      offset = footwear ? 0 : -4;
    } else {
      return raw;
    }

    const cleanNumber = (number) => {
      const rounded = Math.round(number * 10) / 10;
      return Number.isInteger(rounded) ? String(rounded) : String(rounded);
    };

    const first = cleanNumber(Number.parseFloat(match[1]) + offset);
    const second = match[2] ? cleanNumber(Number.parseFloat(match[2]) + offset) : '';
    return `${prefix} ${first}${second ? `–${second}` : ''}`;
  };

  const localizeStaticSizeGuide = () => {
    if (marketCountry === 'AU') return;

    const bodyChartPrefix = marketCountry === 'GB' ? 'UK' : (marketCountry === 'US' || marketCountry === 'NZ' || marketCountry === 'SG' ? 'US' : 'AU');
    if (bodyChartPrefix === 'AU') return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;

    while ((node = walker.nextNode())) {
      const parent = node.parentElement;
      if (!parent || parent.closest('script, style')) continue;

      let text = node.nodeValue || '';
      text = text.replace(/Australian clothing size chart/gi, `${bodyChartPrefix} clothing size chart`);
      text = text.replace(/AU body chart/gi, `${bodyChartPrefix} body chart`);
      text = text.replace(/AU size/gi, `${bodyChartPrefix} size`);
      text = text.replace(/\bAU\s+(\d+(?:\.\d+)?)(?:\s*[-–—]\s*(\d+(?:\.\d+)?))?/g, (_match, first, second) => {
        return formatMarketSize(`AU ${first}${second ? `–${second}` : ''}`, 'tops');
      });
      node.nodeValue = text;
    }
  };

  const toCm = (value) => currentUnit === 'in' ? value * 2.54 : value;
  const fromCm = (value) => currentUnit === 'in' ? value / 2.54 : value;
  const formatValue = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const clearMessages = () => {
    warningBox.textContent = '';
    warningBox.classList.remove('is-visible');
    resultBox.classList.remove('is-visible');
  };

  const showWarning = (message) => {
    warningBox.textContent = message;
    warningBox.classList.add('is-visible');
    resultBox.classList.remove('is-visible');
  };

  const updateContext = (type) => {
    const config = typeConfig[type];
    if (!config) {
      contextBox?.classList.remove('is-visible');
      return;
    }
    if (productTitle) productTitle.textContent = config.label;
    if (productSummary) productSummary.textContent = config.summary;
    if (productPill) productPill.textContent = 'Selected product type';
    contextBox?.classList.add('is-visible');
  };

  const applyType = (type) => {
    currentType = type;
    const config = typeConfig[type];

    measureFields.forEach((field) => {
      field.hidden = true;
      const input = field.querySelector('[data-measure]');
      if (input) input.required = false;
    });

    if (!config) {
      contextBox?.classList.remove('is-visible');
      if (fitField) fitField.hidden = false;
      clearMessages();
      return;
    }

    [...config.required, ...config.optional].forEach((measurement) => {
      const field = root.querySelector(`[data-measure-field="${measurement}"]`);
      const input = root.querySelector(`[data-measure="${measurement}"]`);
      if (field) field.hidden = false;
      if (input) input.required = config.required.includes(measurement);
    });

    if (fitField) fitField.hidden = Boolean(config.accessory || config.footwear || config.oneSize || config.noSize);
    updateContext(type);
    clearMessages();
  };

  const getMeasurements = () => {
    const values = {};
    measureInputs.forEach((input) => {
      const raw = Number.parseFloat(input.value);
      if (Number.isFinite(raw) && raw > 0) values[input.dataset.measure] = toCm(raw);
    });
    return values;
  };

  const renderResult = ({ size, details = [], reason, note }) => {
    resultSize.textContent = formatMarketSize(size);
    if (resultDetails) {
      resultDetails.innerHTML = details.map((detail) => `
        <div class="asg-result-detail">
          <small>${detail.label}</small>
          <strong>${detail.value}</strong>
        </div>
      `).join('');
    }
    if (resultReason) resultReason.textContent = reason || '';
    if (resultNote) resultNote.textContent = note || '';
    warningBox.classList.remove('is-visible');
    resultBox.classList.add('is-visible');
    resultBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const recommendAccessory = (type, measurements) => {
    if (type === 'sun_hats') {
      const head = measurements.head;
      return {
        size: head <= 54 ? 'S' : head <= 57 ? 'M' : head <= 60 ? 'L' : 'XL',
        reason: 'Based on your head circumference.',
        note: 'If the hat is adjustable, use this as your starting point and fine-tune the fastening.'
      };
    }
    if (type === 'arm_sleeves') {
      const bicep = measurements.bicep;
      return {
        size: bicep <= 27 ? 'S' : bicep <= 31 ? 'M' : bicep <= 35 ? 'L' : 'XL',
        reason: 'Based on the circumference of the fullest part of your upper arm.',
        note: 'The sleeve should feel secure without restricting movement.'
      };
    }
    const palm = measurements.palm;
    return {
      size: palm <= 17 ? 'S' : palm <= 19 ? 'M' : palm <= 21 ? 'L' : 'XL',
      reason: 'Based on your palm circumference.',
      note: 'Choose the larger size if you are between measurements or prefer less compression.'
    };
  };

  const recommendFootwear = (measurements) => {
    const row = defaultFootwearChart.find((item) => measurements.foot_length <= item.maxFootLengthCm);
    if (!row) {
      showWarning('Your foot length is above the current footwear chart. Please check the product-specific size table or contact us for sizing help.');
      return null;
    }
    return {
      size: row.size,
      reason: 'Based on the length of your longer foot.',
      note: 'This is a general women’s footwear recommendation. If a product has its own size chart or you are between sizes, use the product-specific chart and choose the larger size.'
    };
  };

  const recommend = () => {
    clearMessages();
    const config = typeConfig[currentType];
    if (!config) {
      showWarning('Select a product type first.');
      typeSelect.focus();
      return;
    }

    if (config.noSize) {
      renderResult({ size: 'No size required', reason: 'This product does not require body measurements.', note: 'You can continue with the product option shown on the product page.' });
      return;
    }
    if (config.oneSize) {
      renderResult({ size: 'One Size', reason: 'This product is designed as a one-size or adjustable item.', note: 'Check the product-specific dimensions and Size & Fit notes for expected coverage.' });
      return;
    }

    const measurements = getMeasurements();
    const missing = config.required.filter((measurement) => !measurements[measurement]);
    if (missing.length) {
      showWarning(`Enter your ${missing.map((item) => measurementLabels[item].toLowerCase()).join(' and ')} to continue.`);
      root.querySelector(`[data-measure="${missing[0]}"]`)?.focus();
      return;
    }

    const details = [...config.required, ...config.optional]
      .filter((measurement) => measurements[measurement])
      .map((measurement) => ({
        label: measurementLabels[measurement],
        value: `${formatValue(fromCm(measurements[measurement]))} ${currentUnit}`
      }));

    if (config.footwear) {
      const footwearResult = recommendFootwear(measurements);
      if (footwearResult) renderResult({ ...footwearResult, details });
      return;
    }
    if (config.accessory) {
      renderResult({ ...recommendAccessory(currentType, measurements), details });
      return;
    }

    let chartIndex = auChart.findIndex((row) => config.primary.every((measurement) => measurements[measurement] <= row[measurement]));
    if (chartIndex === -1) {
      showWarning(`Your measurements are above the current ${formatMarketSize('AU 24', currentType)} chart. Please contact us for personal sizing help.`);
      return;
    }
    if (fitSelect?.value === 'relaxed') chartIndex = Math.min(chartIndex + 1, auChart.length - 1);

    const row = auChart[chartIndex];
    const mainMeasurements = config.primary.map((measurement) => measurementLabels[measurement].toLowerCase()).join(', ');
    renderResult({
      size: `AU ${row.au}`,
      details,
      reason: `Based on your ${mainMeasurements} and a ${fitSelect?.value || 'regular'} fit preference.`,
      note: `Your body-chart recommendation is ${formatMarketSize(`AU ${row.au}`, currentType)}. Product fabrics and individual cuts can vary, so also check the product-specific Size & Fit notes.`
    });
  };

  const setUnit = (nextUnit) => {
    if (nextUnit === currentUnit) return;
    measureInputs.forEach((input) => {
      const value = Number.parseFloat(input.value);
      if (!Number.isFinite(value) || value <= 0) return;
      const valueCm = currentUnit === 'in' ? value * 2.54 : value;
      input.value = formatValue(nextUnit === 'in' ? valueCm / 2.54 : valueCm);
    });
    currentUnit = nextUnit;
    unitButtons.forEach((button) => button.classList.toggle('is-active', button.dataset.asgUnit === currentUnit));
    unitLabels.forEach((label) => { label.textContent = currentUnit === 'cm' ? '(cm)' : '(in)'; });
    clearMessages();
  };

  const reset = () => {
    measureInputs.forEach((input) => { input.value = ''; });
    if (fitSelect) fitSelect.value = 'regular';
    if (currentUnit !== 'cm') setUnit('cm');
    typeSelect.value = '';
    if (typeField) typeField.hidden = false;
    applyType('');
    clearMessages();
  };

  unitButtons.forEach((button) => button.addEventListener('click', () => setUnit(button.dataset.asgUnit)));
  typeSelect.addEventListener('change', () => applyType(typeSelect.value));
  changeTypeButton?.addEventListener('click', () => {
    if (typeField) typeField.hidden = false;
    typeSelect.focus();
  });
  recommendButton.addEventListener('click', recommend);
  resetButton.addEventListener('click', reset);
  measureInputs.forEach((input) => input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      recommend();
    }
  }));

  localizeStaticSizeGuide();
  if (typeField) typeField.hidden = false;
  typeSelect.value = '';
  applyType('');
})();
